import sign from "@/assets/Amigo-INN-RV-park-slide-2-1.webp";
import sign400 from "@/assets/Amigo-INN-RV-park-slide-2-1-400.webp";
import clubhouse from "@/assets/amigo-home-2.webp";
import clubhouse800 from "@/assets/amigo-home-2-800.webp";
import clubhouse1200 from "@/assets/amigo-home-2-1200.webp";
import cabinRow from "@/assets/amigo-home-3.webp";
import cabinRow800 from "@/assets/amigo-home-3-800.webp";
import cabinRow1200 from "@/assets/amigo-home-3-1200.webp";
import rvSite from "@/assets/amigo-home-4.webp";
import rvSite800 from "@/assets/amigo-home-4-800.webp";
import rvSite1200 from "@/assets/amigo-home-4-1200.webp";
import parkModelPatio from "@/assets/amigo-home-5.webp";
import parkModelPatio800 from "@/assets/amigo-home-5-800.webp";
import parkModelPatio1200 from "@/assets/amigo-home-5-1200.webp";
import parkModelCarport from "@/assets/amigo-home-6.webp";
import parkModelCarport800 from "@/assets/amigo-home-6-800.webp";
import parkModelCarport1200 from "@/assets/amigo-home-6-1200.webp";
import coveredParking from "@/assets/amigo-home-7.webp";
import coveredParking800 from "@/assets/amigo-home-7-800.webp";
import coveredParking1200 from "@/assets/amigo-home-7-1200.webp";

function srcSet(...entries: [string, number][]) {
  return entries.map(([src, w]) => `${src} ${w}w`).join(", ");
}

/**
 * Authentic Amigo Inn photography with descriptive alt text
 * written from what is visible in each photograph.
 *
 * Mid-size WebP variants (400/800/1200) let the browser pick a
 * download closer to the displayed CSS size on mobile and tablet.
 */
export const PHOTOS = {
  sign: {
    src: sign,
    srcSet: srcSet([sign400, 400], [sign, 620]),
    width: 620,
    height: 450,
    alt: "The red and yellow Amigo Inn & RV Park roadside sign in front of the park office, framed by palms and grass.",
  },
  clubhouse: {
    src: clubhouse,
    srcSet: srcSet([clubhouse800, 800], [clubhouse1200, 1200], [clubhouse, 1800]),
    width: 1800,
    height: 1200,
    alt: "The Amigo Inn clubhouse with a full-length shuffleboard table, card tables, ceiling fans and neon signs on wood-panelled walls.",
  },
  cabinRow: {
    src: cabinRow,
    srcSet: srcSet([cabinRow800, 800], [cabinRow1200, 1200], [cabinRow, 1652]),
    width: 1652,
    height: 732,
    alt: "A row of cabins and park models along a gravel drive under a bright blue South Texas sky, with a palm tree beside the first unit.",
  },
  rvSite: {
    src: rvSite,
    srcSet: srcSet([rvSite800, 800], [rvSite1200, 1200], [rvSite, 1800]),
    width: 1800,
    height: 1200,
    alt: "A fifth-wheel travel trailer parked on a level site at Amigo Inn with a concrete patio, picnic table and shade trees nearby.",
  },
  parkModelPatio: {
    src: parkModelPatio,
    srcSet: srcSet([parkModelPatio800, 800], [parkModelPatio1200, 1200], [parkModelPatio, 1800]),
    width: 1800,
    height: 1200,
    alt: "A cream park model unit with a wall of windows, green entry steps and a tall metal-covered concrete patio beside it.",
  },
  parkModelCarport: {
    src: parkModelCarport,
    srcSet: srcSet(
      [parkModelCarport800, 800],
      [parkModelCarport1200, 1200],
      [parkModelCarport, 1800],
    ),
    width: 1800,
    height: 1200,
    alt: "A park model home with a private covered carport, banana plants at the corner and open gravel lots behind it.",
  },
  coveredParking: {
    src: coveredParking,
    srcSet: srcSet([coveredParking800, 800], [coveredParking1200, 1200], [coveredParking, 1800]),
    width: 1800,
    height: 1200,
    alt: "A tall steel canopy over a concrete pad used for covered RV and boat storage, with boats parked in the next bay.",
  },
} as const;

export type Photo = (typeof PHOTOS)[keyof typeof PHOTOS];

/** Common `sizes` values for how photos are laid out across the site. */
export const PHOTO_SIZES = {
  fullBleed: "100vw",
  half: "(min-width: 1024px) 45vw, 100vw",
  third: "(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 100vw",
  gridWide: "(min-width: 1024px) 55vw, 100vw",
  gridNarrow: "(min-width: 1024px) 40vw, 100vw",
  gridThird: "(min-width: 1024px) 30vw, 100vw",
  inset: "(min-width: 1024px) 18vw, (min-width: 640px) 28vw, 40vw",
  galleryWide: "(min-width: 640px) 58vw, 100vw",
  galleryNarrow: "(min-width: 640px) 40vw, 100vw",
  galleryThird: "(min-width: 640px) 30vw, 100vw",
} as const;
